import { forwardRef } from 'react';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { Button, IconButton } from '../../buttons';
import { Theme } from '@fagmobler/sanity';

export type ContentCardSize = 'sm' | 'md' | 'lg';

export type ContentCardProps = {
  className?: string;
  buttonText?: string;
  image?: React.ReactNode;
  imagePosition?: 'before' | 'after';
  title?: string;
  pretitle?: string;
  body?: string | React.ReactNode;
  href?: string;
  size?: ContentCardSize;
  theme?: Theme;
  onClick?(): void;
};

export const ContentCard = forwardRef(function ContentCard(
  {
    className = '',
    buttonText,
    image,
    imagePosition = 'before',
    title = '',
    pretitle,
    body,
    href,
    size = 'lg',
    theme = 'neutral',
    onClick,
    ...rest
  }: ContentCardProps,
  ref
) {
  const small = size === 'sm';
  const large = size === 'lg' || size == 'md';

  const withImage = !!image;

  const block = (
    <article
      className={clsx(
        'flex',
        'group',
        'relative',
        'w-full',

        'flex-col',
        {
          'bg-neutral-40 text-neutral-80': theme === 'neutral',
          'text-neutral-0 bg-neutral-80': theme === 'neutralDark',
          'bg-brown-30 text-neutral-80': theme === 'lightBrown',
          'bg-tetiery-30 text-neutral-80': theme === 'lightYellow',
          'bg-green-40 text-neutral-80': theme === 'lightGreen',
          'bg-tetiery-60 text-neutral-80': theme === 'blackWeekYellow',
          'bg-neutral-80 text-tetiery-50': theme === 'blackWeekBlack',
          'bg-brown-70 text-gold-40': theme === 'nobel',
          'bg-brown-70 text-pink-60': theme === 'garden',
          'md:flex-row': large,
          'max-w-[480px]': small,
          'md:flex-row-reverse': imagePosition === 'after' && large,
          'flex-col': imagePosition === 'before' && small,
        },
        className
      )}
    >
      <div
        className={clsx('relative h-[350px]', {
          'md:w-[50%]': large,
          'md:h-[500px]': large,
          'h-[350px]': small,
        })}
      >
        {withImage && (
          <figure
            className={clsx('w-full', 'h-full', 'object-cover-child-img')}
          >
            {typeof image === 'string' ? (
              <img src={image} alt="Preview image" />
            ) : (
              image
            )}
          </figure>
        )}
      </div>
      <div
        className={clsx('flex', 'justify-between', 'flex-col', {
          'w-full': !large,
          'md:w-[50%]': large,
          'p-4': small,
          'p-10': large,
        })}
      >
        <div
          className={clsx({
            'pb-4': small,
          })}
        >
          <header>
            <div className="empty:hidden hd-md lg:hd-lg xl:hd-2xl font-normal">
              {pretitle}
            </div>
            {title && (
              <h3 className="hd-lg sm:hd-xl md:hd-xl lg:hd-3xl xl:hd-5xl 2xl:hd-6xl mb-3 break-words group-hover:underline">
                {title}
              </h3>
            )}
          </header>
          {body && <div className="body-md mb-10">{body}</div>}
        </div>
        {href && (!buttonText || small) && (
          <IconButton
            size={small ? 'base' : 'md'}
            variant={
              theme === 'neutralDark' ||
              theme === 'nobel' ||
              theme === 'garden' ||
              theme === 'blackWeekBlack'
                ? 'ghost'
                : undefined
            }
          >
            <ArrowRightIcon />
          </IconButton>
        )}
        {(href || onClick) && buttonText && !small && (
          <Button className="inline-block max-w-fit pl-4" onClick={onClick}>
            {buttonText}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </Button>
        )}
      </div>
    </article>
  );

  return block;
});

export default ContentCard;
